import { Route, Routes } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ToastProvider } from './components/ui/Toast'
import { ConfirmProvider } from './components/ui/ConfirmModal'
import AppLayout from './components/AppLayout'
import AdminRoute from './components/AdminRoute'
import ProtectedRoute from './components/ProtectedRoute'
import StaffRoute from './components/StaffRoute'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Register from './pages/Register'
import VerifyEmail from './pages/VerifyEmail'
import ConfirmEmail from './pages/ConfirmEmail'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import NotFound from './pages/NotFound'
import ClientList from './pages/clients/ClientList'
import ClientForm from './pages/clients/ClientForm'
import ClientDetail from './pages/clients/ClientDetail'
import PatientList from './pages/patients/PatientList'
import PatientForm from './pages/patients/PatientForm'
import PatientDetail from './pages/patients/PatientDetail'
import VaccinationForm from './pages/patients/VaccinationForm'
import ConsultationForm from './pages/consultations/ConsultationForm'
import ConsultationDetail from './pages/consultations/ConsultationDetail'
import AppointmentList from './pages/appointments/AppointmentList'
import AppointmentForm from './pages/appointments/AppointmentForm'
import AppointmentDetail from './pages/appointments/AppointmentDetail'
import InvoiceList from './pages/invoices/InvoiceList'
import InvoiceForm from './pages/invoices/InvoiceForm'
import InvoiceDetail from './pages/invoices/InvoiceDetail'
import InvoicePrint from './pages/invoices/InvoicePrint'
import PrescriptionPrint from './pages/consultations/PrescriptionPrint'
import VaccinationCertificate from './pages/patients/VaccinationCertificate'
import StaffList from './pages/practices/StaffList'
import ServiceCatalog from './pages/practices/ServiceCatalog'
import AcceptInvite from './pages/AcceptInvite'
import AcceptPortalInvite from './pages/AcceptPortalInvite'
import Unsubscribe from './pages/Unsubscribe'
import UserProfile from './pages/profile/UserProfile'
import ProductList from './pages/stock/ProductList'
import ProductForm from './pages/stock/ProductForm'
import StockList from './pages/stock/StockList'
import StockReceiveForm from './pages/stock/StockReceiveForm'
import DrugRegister from './pages/stock/DrugRegister'
import StandaloneDispensePage from './pages/stock/DispenseForm'
import UserList from './pages/admin/users/UserList'
import UserDetail from './pages/admin/users/UserDetail'
import OrgList from './pages/admin/organizations/OrgList'
import OrgForm from './pages/admin/organizations/OrgForm'
import PracticeList from './pages/admin/practices/PracticeList'
import PracticeForm from './pages/admin/practices/PracticeForm'
import StaffApplicationList from './pages/admin/StaffApplicationList'

export default function App() {
  return (
    <ErrorBoundary>
    <ToastProvider>
    <ConfirmProvider>
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/accept-portal-invite" element={<AcceptPortalInvite />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />

        {/* Protected: any authenticated, verified user */}
        <Route element={<ProtectedRoute />}>
          {/* Print pages — no chrome/sidebar */}
          <Route path="/invoices/:id/print" element={<InvoicePrint />} />
          <Route path="/consultations/:id/prescription" element={<PrescriptionPrint />} />
          <Route path="/patients/:id/vaccination-certificate" element={<VaccinationCertificate />} />

          <Route element={<AppLayout />}>
            {/* Accessible to all authenticated users */}
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Staff only — 'user' role → /profile, 'client' role → /dashboard */}
            <Route element={<StaffRoute />}>
              {/* Clients */}
              <Route path="/clients" element={<ClientList />} />
              <Route path="/clients/new" element={<ClientForm />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/clients/:id/edit" element={<ClientForm />} />

              {/* Patients */}
              <Route path="/patients" element={<PatientList />} />
              <Route path="/patients/:id" element={<PatientDetail />} />
              <Route path="/clients/:id/patients/new" element={<PatientForm />} />
              <Route path="/clients/:id/patients/:pid/edit" element={<PatientForm />} />

              {/* Consultations */}
              <Route path="/patients/:id/consultations/new" element={<ConsultationForm />} />
              <Route path="/consultations/:id" element={<ConsultationDetail />} />

              {/* Vaccinations */}
              <Route path="/patients/:id/vaccinations/new" element={<VaccinationForm />} />

              {/* Appointments */}
              <Route path="/appointments" element={<AppointmentList />} />
              <Route path="/appointments/new" element={<AppointmentForm />} />
              <Route path="/appointments/:id" element={<AppointmentDetail />} />
              <Route path="/appointments/:id/edit" element={<AppointmentForm />} />
              <Route path="/patients/:patientId/appointments/new" element={<AppointmentForm />} />

              {/* Invoices */}
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/new" element={<InvoiceForm />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />

              {/* Staff */}
              <Route path="/practices/:id/staff" element={<StaffList />} />
              <Route path="/practices/:id/services" element={<ServiceCatalog />} />

              {/* Stock */}
              <Route path="/stock" element={<StockList />} />
              <Route path="/stock/batches/new" element={<StockReceiveForm />} />
              <Route path="/stock/products" element={<ProductList />} />
              <Route path="/stock/products/new" element={<ProductForm />} />
              <Route path="/stock/products/:id/edit" element={<ProductForm />} />
              <Route path="/stock/register" element={<DrugRegister />} />
              <Route path="/stock/register/:productId" element={<DrugRegister />} />
              <Route path="/stock/dispense" element={<StandaloneDispensePage />} />

              {/* Admin — super_admin only */}
              <Route element={<AdminRoute />}>
                <Route path="/admin/users" element={<UserList />} />
                <Route path="/admin/users/:id" element={<UserDetail />} />
                <Route path="/admin/organizations" element={<OrgList />} />
                <Route path="/admin/organizations/new" element={<OrgForm />} />
                <Route path="/admin/organizations/:id/edit" element={<OrgForm />} />
                <Route path="/admin/practices" element={<PracticeList />} />
                <Route path="/admin/practices/new" element={<PracticeForm />} />
                <Route path="/admin/practices/:id/edit" element={<PracticeForm />} />
                <Route path="/admin/staff-applications" element={<StaffApplicationList />} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
    </ConfirmProvider>
    </ToastProvider>
    </ErrorBoundary>
  )
}
